'use client';

import { Car, Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '@/components/shared';
import {
  Badge,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import type { AdminVehicle, OrganizationFleet } from '@/lib/types';
import { UnitDialog } from './UnitDialog';

/** The customer's units, with every property editable from here. */
export function UnitsTable({ fleet }: { fleet: OrganizationFleet }) {
  const [editing, setEditing] = useState<AdminVehicle | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const open = (vehicle: AdminVehicle | null) => {
    setEditing(vehicle);
    setIsOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => open(null)}>
          <Plus className="mr-2 h-4 w-4" />
          New unit
        </Button>
      </div>

      {fleet.data.length === 0 ? (
        <EmptyState
          icon={Car}
          title="No units yet"
          description="Allocate a device from the partner's pool, or create a unit without one."
          action={
            <Button onClick={() => open(null)}>
              <Plus className="mr-2 h-4 w-4" />
              Create unit
            </Button>
          }
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Unit</TableHead>
                <TableHead>Ident</TableHead>
                <TableHead>Class</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fleet.data.map((vehicle) => (
                <TableRow key={vehicle.id}>
                  <TableCell>
                    <p className="font-medium">{vehicle.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {vehicle.plateNumber}
                      {vehicle.brand ? ` · ${vehicle.brand} ${vehicle.model ?? ''}`.trimEnd() : ''}
                    </p>
                  </TableCell>
                  <TableCell>
                    {vehicle.ident ? (
                      <code className="text-xs">{vehicle.ident}</code>
                    ) : (
                      <Badge variant="secondary">None</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {vehicle.assetClass === 'POWERED' ? 'Powered' : 'Unpowered'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {vehicle.PartnerDevice ? 'Allocated from a pool' : 'Created here'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={vehicle.isActive ? 'success' : 'secondary'}>
                      {vehicle.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => open(vehicle)}>
                      <Pencil className="mr-1 h-4 w-4" />
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <UnitDialog
        organizationId={fleet.organization.id}
        vehicle={editing}
        open={isOpen}
        onOpenChange={setIsOpen}
        groups={fleet.groups}
        drivers={fleet.drivers}
      />
    </div>
  );
}
